import axios from "axios";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { BASE_URL } from "../../../env";
import Sidebar from "../../../components/layout/Sidebar";
import { Row, Col, Form } from "react-bootstrap";
import {
  generateLoanSummaryForMember,
  getFirstAndLastEmiDates,
  formatAmount,
} from "../../../utils/utils";
import Select from "react-select";
import CustomPagination from "../../../components/ui/CustomPagination";
import DataTable from "../../../components/ui/DataTable";
import { useAdminAuth } from "../../auth/AdminAuthProvider";
import DashboardHeader from "../../../components/layout/DashboardHeader";
import { MdExpandMore } from "react-icons/md";
import StandardizedDateInput from "../../../components/ui/StandardizedDateInput";

const GplParReport = () => {
  const { adminUser } = useAdminAuth();

  // State variables
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openSidebarToggle, setOpenSidebarToggle] = useState(window.innerWidth > 1000);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOverdueFilter, setSelectedOverdueFilter] = useState(null);
  const [selectedBranchFilter, setSelectedBranchFilter] = useState(null);
  const [selectedEmployeeFilter, setSelectedEmployeeFilter] = useState(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [fromDateDisbursed, setFromDateDisbursed] = useState("");
  const [toDateDisbursed, setToDateDisbursed] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Constants
  // Not currently read - this screen passes the literal "2000-01-01" to
  // generateLoanSummaryForMember below. Kept in step with that literal so wiring
  // it up later cannot silently truncate a loan's EMI schedule.
  const fromDate = new Date("2000-01-01");
  const toDate = new Date();
  const itemsPerPage = 20;

  // Pagination calculations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Sidebar toggle function
  const OpenSidebar = () => {
    setOpenSidebarToggle(!openSidebarToggle);
  };

  // Data fetching effect
  useEffect(() => {
    const getReportData = async (id) => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${BASE_URL}/api/getGploutstandingreport`,
          { params: { id } }
        );
        if (response.data) {
          setReportData(response.data);
        } else {
          setError("No data received from server");
        }
      } catch (error) {
        console.error("Error fetching Data:", error);
        setError(error.response?.data?.message || "Failed to fetch report data");
      } finally {
        setLoading(false);
      }
    };

    if (adminUser?.user?.id) {
      getReportData(adminUser.user.id);
    }
  }, [adminUser?.user?.id]);

  // Data processing - Using enhanced summary function
  const flattenedData = useMemo(() => {
    if (!reportData || reportData.length === 0) return [];

    // PAR means Portfolio At Risk, so only loans that are actually overdue
    // belong here. This previously kept every disbursed loan, which reported
    // healthy loans as at-risk. Matches general/ParReport.js (Business Loan).
    return reportData
      .map((data) => generateLoanSummaryForMember(data, "2000-01-01", toDate.toISOString().split('T')[0]))
      .filter((result) => result !== null && result.dpd > 0);
  }, [reportData]);

  // Filter function
  const filterData = useCallback((data) => {
    let filtered = [...data];

    // Overdue days filter
    if (selectedOverdueFilter) {
      filtered = filtered.filter((item) => {
        const overdueDays = item.overdueDays ?? 0;
        switch (selectedOverdueFilter.value) {
          case "0-30":
            return overdueDays >= 0 && overdueDays <= 30;
          case "31-60":
            return overdueDays >= 31 && overdueDays <= 60;
          case "61-90":
            return overdueDays >= 61 && overdueDays <= 90;
          case "91-180":
            return overdueDays >= 91 && overdueDays <= 180;
          case "181-365":
            // Starts at 181, not 180: a loan sitting on exactly 180 days used to
            // match both this bucket and "91-180" and was counted twice.
            return overdueDays >= 181 && overdueDays <= 365;
          case ">365":
            return overdueDays > 365;
          default:
            return true;
        }
      });
    }

    // Date range filter on Disbursement Date
    if (fromDateDisbursed && toDateDisbursed) {
      const from = new Date(fromDateDisbursed);
      const to = new Date(toDateDisbursed);
      to.setHours(23, 59, 59, 999);

      filtered = filtered.filter((item) => {
        if (!item.branchManagerStatusUpdatedAt) return false;
        const updatedAt = new Date(item.branchManagerStatusUpdatedAt);
        return updatedAt >= from && updatedAt <= to;
      });
    }

    // Branch filter
    if (selectedBranchFilter) {
      filtered = filtered.filter((item) => item?.branchName === selectedBranchFilter.value);
    }

    // Employee filter
    if (selectedEmployeeFilter) {
      filtered = filtered.filter((item) => item?.employeeName === selectedEmployeeFilter.value);
    }

    if (searchTerm && searchTerm.trim() !== "") {
      const term = searchTerm.trim().toLowerCase();
      filtered = filtered.filter((item) => {
        const memberName = String(item?.memberName || "").toLowerCase();
        const customerId = String(item?.customerId || "").toLowerCase();
        const loanId = String(item?.loanId || "").toLowerCase();
        const phoneNumber = String(item?.phoneNumber || "").toLowerCase();
        const aadharNo = String(item?.aadharNo || "").toLowerCase();
        const applicationId = String(item?.ApplicationId || "").toLowerCase();

        return (
          memberName.includes(term) ||
          customerId.includes(term) ||
          loanId.includes(term) ||
          phoneNumber.includes(term) ||
          aadharNo.includes(term) ||
          applicationId.includes(term)
        );
      });
    }

    return filtered;
  }, [selectedOverdueFilter, fromDateDisbursed, toDateDisbursed, selectedBranchFilter, selectedEmployeeFilter, searchTerm]);

  // Apply filters memoized
  const allFilteredData = useMemo(() => filterData(flattenedData), [filterData, flattenedData]);
  const totalPages = Math.ceil(allFilteredData.length / itemsPerPage);
  const paginatedData = useMemo(() => allFilteredData.slice(indexOfFirstItem, indexOfLastItem), [allFilteredData, indexOfFirstItem, indexOfLastItem]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedOverdueFilter, selectedBranchFilter, selectedEmployeeFilter, fromDateDisbursed, toDateDisbursed, searchTerm]);

  const columns = useMemo(() => [
    { label: "S No", render: (data, index, isExcel) => isExcel ? index + 1 : indexOfFirstItem + index + 1 },
    { label: "Branch Name", render: (data) => data?.branchName ?? "" },
    { label: "Customer Name", render: (data) => data?.memberName ?? "" },
    { label: "Customer ID", render: (data) => data?.customerId ?? "" },
    { label: "Loan Account No", render: (data) => data?.loanId ?? "" },
    { label: "Loan Amount", render: (data) => formatAmount(data?.eligibleLoanAmountByBm) },
    { label: "O/S Principal", render: (data) => formatAmount(data?.totalOutstandingPrincipal) },
    { label: "O/S Interest", render: (data) => formatAmount(data?.totalOutstandingInterest) },
    { label: "Total O/S", render: (data) => formatAmount((data?.totalOutstandingPrincipal || 0) + (data?.totalOutstandingInterest || 0)) },
    { label: "O/D Principal", render: (data) => formatAmount(data?.overduePrincipal) },
    { label: "O/D Interest", render: (data) => formatAmount(data?.overdueInterest) },
    { label: "Total O/D", render: (data) => formatAmount((data?.overduePrincipal || 0) + (data?.overdueInterest || 0)) },
    { label: "DPD", render: (data) => data?.dpd ?? 0 },
    { label: "Bucket", render: (data) => data?.bucket ?? "0" },
    { label: "Employee Name", render: (data) => data?.employeeName ?? "" },
  ], [indexOfFirstItem]);

  const branchOptions = useMemo(() => {
    return [...new Set(flattenedData.map((data) => data?.branchName))]
      .filter(Boolean)
      .map((name) => ({ value: name, label: name }));
  }, [flattenedData]);

  const employeeOptions = useMemo(() => {
    return [...new Set(flattenedData.map((data) => data?.employeeName))]
      .filter(Boolean)
      .map((name) => ({ value: name, label: name }));
  }, [flattenedData]);

  const overdueFilters = [
    { value: "0-30", label: "0 - 30 days" },
    { value: "31-60", label: "31 - 60 days" },
    { value: "61-90", label: "61 - 90 days" },
    { value: "91-180", label: "91 - 180 days" },
    { value: "181-365", label: "181 - 365 days" },
    { value: ">365", label: "> 365 days" },
  ];

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedOverdueFilter(null);
    setSelectedBranchFilter(null);
    setSelectedEmployeeFilter(null);
    setFromDateDisbursed("");
    setToDateDisbursed("");
    setCurrentPage(1);
  };

  return (
    <section className="master-layout">
      <div className="master-sidebar-wrapper">
        <Sidebar
          openSidebarToggle={openSidebarToggle}
          OpenSidebar={OpenSidebar}
        />
      </div>

      <div className="master-content-wrapper">
        <div className="master-header-wrapper">
          <DashboardHeader OpenSidebar={OpenSidebar} />
        </div>

        <div className="master-card-container">
          <div className="master-search-section">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h1 className="TableHeaderText w-100 text-start">GPL PAR Report</h1>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={clearAllFilters}
                  disabled={!selectedOverdueFilter && !selectedBranchFilter && !selectedEmployeeFilter && !fromDateDisbursed && !toDateDisbursed}
                >
                  Clear Filters
                </button>
              </div>
            </div>

            <div className="w-100">
              <Row className="w-100" style={{ fontWeight: "500" }}>
                <Col xs={6} lg={2} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>From Date</Form.Label>
                    <StandardizedDateInput
                      value={fromDateDisbursed}
                      onChange={(event) => {
                        const val = event.target.value;
                        setFromDateDisbursed(val);
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={6} lg={2} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>To Date</Form.Label>
                    <StandardizedDateInput
                      value={toDateDisbursed}
                      onChange={(event) => {
                        const val = event.target.value;
                        setToDateDisbursed(val);
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            <button
              className={`filter-toggle-btn ${showMobileFilters ? "active" : ""}`}
              onClick={() => setShowMobileFilters(!showMobileFilters)}
            >
              <span style={{ fontWeight: 600, fontSize: "14px" }}>
                {showMobileFilters ? "Hide Filters" : "Show Filters"}
              </span>
              <MdExpandMore size={22} />
            </button>

            <div className={`w-100 ${!showMobileFilters ? "mobile-hidden" : "mobile-visible"}`}>
              <Row className="w-100" style={{ fontWeight: "500" }}>
                <Col xs={12} lg={4} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>Search Customer</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Search Name, Customer ID, Loan Acc No, Phone..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} lg={4} md={6} className="mb-2">
                  <Form.Label>Overdue Days</Form.Label>
                  <Select
                    value={selectedOverdueFilter}
                    onChange={(val) => {
                      setSelectedOverdueFilter(val);
                      if (fromDateDisbursed && toDateDisbursed && selectedBranchFilter && selectedEmployeeFilter) {
                        setTimeout(() => setShowMobileFilters(false), 300);
                      }
                    }}
                    options={overdueFilters}
                    isClearable
                    placeholder="Filter by Overdue Days"
                    className="filter-dropdown"
                  />
                </Col>
                <Col xs={12} lg={4} md={6} className="mb-2">
                  <Form.Label>Branch</Form.Label>
                  <Select
                    value={selectedBranchFilter}
                    onChange={(val) => {
                      setSelectedBranchFilter(val);
                      if (fromDateDisbursed && toDateDisbursed && val && selectedEmployeeFilter) {
                        setTimeout(() => setShowMobileFilters(false), 300);
                      }
                    }}
                    options={branchOptions}
                    isClearable
                    placeholder="Filter by Branch Name"
                    className="filter-dropdown"
                  />
                </Col>
                <Col xs={12} lg={4} md={6} className="mb-2">
                  <Form.Label>Employee</Form.Label>
                  <Select
                    value={selectedEmployeeFilter}
                    onChange={(val) => {
                      setSelectedEmployeeFilter(val);
                      if (fromDateDisbursed && toDateDisbursed && selectedBranchFilter && val) {
                        setTimeout(() => setShowMobileFilters(false), 300);
                      }
                    }}
                    options={employeeOptions}
                    isClearable
                    placeholder="Filter by Employee Name"
                    className="filter-dropdown"
                  />
                </Col>
                <Col xs={12} lg={8} md={12} className="mb-2 d-flex align-items-end">
                  <small className="text-muted">
                    Showing {paginatedData.length} of {allFilteredData.length} records
                    {allFilteredData.length !== flattenedData.length &&
                      ` (filtered from ${flattenedData.length} total)`
                    }
                  </small>
                </Col>
              </Row>
            </div>
          </div>

          <div className="master-list-section">
            <div className="master-table-wrapper" style={{ padding: "0 15px" }}>
              {loading ? (
                <div className="d-flex justify-content-center align-items-center h-100">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : error ? (
                <div className="alert alert-danger" role="alert">
                  <strong>Error:</strong> {error}
                  <button
                    className="btn btn-outline-danger btn-sm ms-3"
                    onClick={() => window.location.reload()}
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <DataTable
                  columns={columns}
                  data={paginatedData}
                  reportName="GplParReport"
                  excelData={allFilteredData}
                />
              )}
            </div>

            <div className="master-pagination-wrapper">
              {!loading && !error && totalPages > 1 && (
                <CustomPagination
                  totalPages={totalPages}
                  currentPage={currentPage}
                  paginate={paginate}
                totalItems={allFilteredData.length}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GplParReport;